import React, { useState, useEffect } from 'react';
import TemplateSidebar from './TemplateSidebar';
import TemplateGrid from './TemplateGrid';
import { categories, templatesData } from '../../data/templatesData';
import FadeIn from '../common/FadeIn';
import { HiAdjustmentsHorizontal, HiChevronDown, HiXMark } from 'react-icons/hi2';
import { IoIosCheckmark } from "react-icons/io";

const INITIAL_COUNT = 12;

const TemplatesSection = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredTemplates, setFilteredTemplates] = useState(templatesData);
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
    if (activeCategory === "all") {
      setFilteredTemplates(templatesData);
    } else {
      setFilteredTemplates(
        templatesData.filter((template) => template.category === activeCategory)
      );
    }
  }, [activeCategory]);

  const currentCatObj = categories.find((c) => c.id === activeCategory) || categories[0];

  const displayedTemplates = filteredTemplates.slice(0, visibleCount);
  const hasMore = visibleCount < filteredTemplates.length;

  const toggleVisible = () => {
    if (hasMore) {
      setVisibleCount(filteredTemplates.length);
    } else {
      setVisibleCount(INITIAL_COUNT);
    }
  };

  return (
    <section className="w-full bg-[#f9f9f5] px-4 py-8 sm:px-8 sm:py-12 lg:px-12 lg:py-16 text-[#1C1917] min-h-screen">
      <div className="max-w-[1440px] mx-auto">
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-10 xl:gap-14 items-start relative">

          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:w-[260px] xl:w-[290px] flex-shrink-0 lg:sticky lg:top-[90px] lg:self-start z-30 pt-1">
            <TemplateSidebar
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              totalItemsCount={filteredTemplates.length}
              templatesData={templatesData}
            />
          </div>

          {/* Main Grid Area */}
          <div className="flex-1 w-full min-w-0">

            {/* Mobile Header Title */}
            <div className="lg:hidden mt-1 mb-4">
              <h1 className="font-saolDisplayItalic text-[28px] sm:text-[34px] font-normal text-[#1C1917] leading-[1.15]">
                Find the perfect match for your brand
              </h1>
            </div>

            {/* Mobile Filter Button */}
            <div className="flex lg:hidden flex-row items-center justify-between gap-2 mb-6 relative z-30">

              <button
                onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-[#1C1917]/20 rounded-md text-[#1C1917] shadow-2xs cursor-pointer transition-transform"
              >
                <HiAdjustmentsHorizontal className="w-4 h-4 text-[#1C1917]" />
                <span className="font-interDisplayMedium text-[11px] uppercase tracking-[0.1em] font-bold truncate max-w-[170px] sm:max-w-[220px]">
                  {currentCatObj.label}
                </span>
                <HiChevronDown className={`w-3.5 h-3.5 text-[#8B8477] transition-transform duration-300 ${isMobileFilterOpen ? "rotate-180" : ""}`} />
              </button>

              <span className="text-[10px] font-bold uppercase tracking-wider text-[#1C1917] bg-white px-3 py-2 border border-black/10 rounded-md shadow-2xs flex-shrink-0">
                {filteredTemplates.length} {filteredTemplates.length === 1 ? "Item" : "Items"}
              </span>

            </div>

            {/* Mobile Filter Dropdown Menu */}
            {isMobileFilterOpen && (
              <div className="lg:hidden mb-6 bg-white border border-[#1C1917]/15 rounded-lg shadow-lg overflow-hidden p-2 flex flex-col gap-1 z-40 animate-fadeIn">
                <div className="flex items-center justify-between px-3 py-2 border-b border-[#1C1917]/10 mb-1">
                  <span className="font-interDisplayMedium text-[10px] uppercase tracking-[0.15em] font-bold text-[#8B8477]">
                    Select Category
                  </span>
                  <button onClick={() => setIsMobileFilterOpen(false)} className="p-1 text-[#8B8477] hover:text-[#1C1917]">
                    <HiXMark className="w-4 h-4" />
                  </button>
                </div>
                {categories.map((cat) => {
                  const isActive = activeCategory === cat.id;
                  const isDisabled = cat.id !== "all";
                  const catCount = cat.id === 'all' 
                    ? templatesData.length 
                    : templatesData.filter(t => t.category === cat.id).length;

                  return (
                    <button
                      key={cat.id}
                      disabled={isDisabled}
                      onClick={() => {
                        if (!isDisabled) {
                          setActiveCategory(cat.id);
                          setIsMobileFilterOpen(false);
                        }
                      }}
                      className={`w-full px-3 py-2.5 text-left font-interDisplayMedium text-[11px] uppercase tracking-[0.1em] rounded-md transition-colors flex items-center justify-between ${
                        isDisabled
                          ? "text-[#A8A196] opacity-50 cursor-not-allowed pointer-events-none"
                          : isActive
                          ? "bg-[#1C1917] text-white font-bold cursor-pointer"
                          : "text-[#1C1917] hover:bg-[#f9f9f5] cursor-pointer"
                        }`}
                    >
                      <span>{cat.label} ({catCount})</span>
                      {isActive && <IoIosCheckmark className="w-5 h-5" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Template Grid */}
            <div key={activeCategory} className="w-full">
              <TemplateGrid templates={displayedTemplates} />
            </div>

            {/* Show More / Show Less Button */}
            {filteredTemplates.length > INITIAL_COUNT && (
              <FadeIn className="w-full mt-10 sm:mt-12 flex justify-center">
                <button
                  onClick={toggleVisible}
                  className="px-8 py-3 bg-[#0D0C0B] text-white font-interDisplayMedium text-[9px] lg:text-[11px] uppercase tracking-[0.15em] font-semibold rounded-md shadow-2xs cursor-pointer hover:bg-[#ccc3ad] hover:text-black duration-300 transition-colors"
                >
                  {hasMore ? "SHOW MORE TEMPLATES" : "SHOW LESS TEMPLATES"}
                </button>
              </FadeIn>
            )}

          </div>

        </div>
      </div>
    </section>
  );
};

export default TemplatesSection;

